import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";

interface ComboboxProps<T> {
  items: T[];
  value: T | null;
  onChange: (item: T) => void;
  getItemKey: (item: T) => string;
  getSearchableText: (item: T) => string;
  renderTrigger: (item: T | null, isOpen: boolean) => React.ReactNode;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Combobox<T>({
  items,
  value,
  onChange,
  getItemKey,
  getSearchableText,
  renderTrigger,
  renderItem,
  placeholder = "Search...",
  emptyMessage = "No items found.",
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ComboboxProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
        >
          {renderTrigger(value, open)}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command className="w-full">
          <CommandInput placeholder={placeholder} className="h-10" />
          <CommandList className="p-2">
            <CommandEmpty className="px-3 py-2">{emptyMessage}</CommandEmpty>
            <CommandGroup className="p-0">
              {items.map((item) => {
                const isSelected = value
                  ? getItemKey(item) === getItemKey(value)
                  : false;
                return (
                  <CommandItem
                    key={getItemKey(item)}
                    value={getSearchableText(item)}
                    onSelect={() => {
                      onChange(item);
                      setOpen(false);
                    }}
                    className="px-3 py-2 group data-[selected=true]:bg-neutral-700/50 data-[selected=true]:text-neutral-300"
                  >
                    {renderItem(item, isSelected)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
